import { useCallback, useEffect, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, Printer } from 'lucide-react';
import { printLabSummary, rupee } from './labPrint';

const toD = (s) => new Date(`${s}T00:00:00Z`);
const toS = (d) => d.toISOString().slice(0, 10);
const addDays = (s, n) => { const d = toD(s); d.setUTCDate(d.getUTCDate() + n); return toS(d); };

export const periodRange = (period, anchor) => {
  if (period === 'week') {
    const start = addDays(anchor, -((toD(anchor).getUTCDay() + 6) % 7));
    return { start, end: addDays(start, 6) };
  }
  if (period === 'month') {
    const d = toD(anchor);
    const start = toS(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1)));
    return { start, end: toS(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0))) };
  }
  return { start: anchor, end: anchor };
};

const shift = (period, anchor, dir) => {
  if (period === 'month') {
    const d = toD(anchor);
    return toS(new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + dir, 1)));
  }
  return addDays(anchor, dir * (period === 'week' ? 7 : 1));
};

const fmt = (s) => toD(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' });
export const rangeLabel = (period, { start, end }) => {
  if (period === 'month') return toD(start).toLocaleDateString('en-IN', { month: 'long', year: 'numeric', timeZone: 'UTC' });
  return start === end ? fmt(start) : `${fmt(start)} – ${fmt(end)}`;
};

const PERIODS = [{ id: 'day', label: 'Day' }, { id: 'week', label: 'Week' }, { id: 'month', label: 'Month' }];

const Stat = ({ label, value, tone = 'text-slate-900', testId }) => (
  <div className="rounded-xl border bg-white p-3">
    <p className="text-[11px] uppercase tracking-wide text-slate-500">{label}</p>
    <p className={`text-xl font-black ${tone}`} data-testid={testId}>{value}</p>
  </div>
);

export const LabSummary = ({ api, today, refreshKey }) => {
  const [period, setPeriod] = useState('day');
  const [anchor, setAnchor] = useState(today);
  const [s, setS] = useState(null);
  const range = periodRange(period, anchor);
  const label = rangeLabel(period, range);
  const load = useCallback(
    () => api.get('/summary', { start: range.start, end: range.end }).then(setS).catch(() => setS(null)),
    [api, range.start, range.end],
  );
  useEffect(() => { load(); }, [load, refreshKey]);
  const atLatest = range.end >= today;

  return (
    <Card className="p-5 space-y-4" data-testid="lab-summary">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="inline-flex rounded-lg border overflow-hidden" data-testid="lab-summary-period">
          {PERIODS.map((p) => (
            <button key={p.id} type="button" onClick={() => setPeriod(p.id)} data-testid={`lab-period-${p.id}`}
              className={`px-3 py-1.5 text-xs font-bold ${period === p.id ? 'bg-teal-700 text-white' : 'bg-white text-slate-600'}`}>{p.label}</button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <Button size="sm" variant="outline" className="h-8 px-2" onClick={() => setAnchor(shift(period, anchor, -1))} data-testid="lab-summary-prev"><ChevronLeft className="h-4 w-4" /></Button>
          {period === 'month'
            ? <Input type="month" value={anchor.slice(0, 7)} max={today.slice(0, 7)} onChange={(e) => e.target.value && setAnchor(`${e.target.value}-01`)} className="h-8 w-36" data-testid="lab-summary-month" />
            : <Input type="date" value={anchor} max={today} onChange={(e) => e.target.value && setAnchor(e.target.value)} className="h-8 w-36" data-testid="lab-summary-date" />}
          <Button size="sm" variant="outline" className="h-8 px-2" disabled={atLatest} onClick={() => setAnchor(shift(period, anchor, 1))} data-testid="lab-summary-next"><ChevronRight className="h-4 w-4" /></Button>
          <Button size="sm" className="h-8 bg-slate-900 text-white" disabled={!s} onClick={() => printLabSummary(s, period, label)} data-testid="lab-summary-print"><Printer className="h-4 w-4 mr-1" /> Print</Button>
        </div>
      </div>
      <h3 className="font-bold" data-testid="lab-summary-range">{PERIODS.find((p) => p.id === period).label} Tally — {label}</h3>
      {s && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            <Stat label="Bills" value={s.bill_count} testId="lab-sum-bills" />
            <Stat label="Total Billed" value={rupee(s.billed)} testId="lab-sum-billed" />
            <Stat label="Cash" value={rupee(s.cash)} testId="lab-sum-cash" />
            <Stat label="UPI" value={rupee(s.upi)} testId="lab-sum-upi" />
            <Stat label="Total Collected" value={rupee(s.collected)} tone="text-emerald-700" testId="lab-sum-collected" />
            <Stat label="Balance Due" value={rupee(s.balance_due)} tone={s.balance_due > 0 ? 'text-red-600' : 'text-slate-900'} testId="lab-sum-due" />
          </div>
          {s.old_dues_collected > 0 && <p className="text-xs text-slate-500">Collected includes {rupee(s.old_dues_collected)} of dues from earlier bills.</p>}
          {s.nursing_collected > 0 && (
            <div className="border rounded-lg divide-y text-sm bg-sky-50" data-testid="lab-sum-nursing">
              {s.from_nursing.filter((n) => n.count || n.cash || n.upi).map((n) => (
                <div key={n.name} className="flex justify-between px-3 py-1.5"><span className="font-semibold">{n.name} (from nursing) × {n.count}</span><span className="text-slate-600">Cash {rupee(n.cash)} • UPI {rupee(n.upi)}</span></div>
              ))}
            </div>
          )}
          {s.tests.length > 0 && (
            <div className="border rounded-lg divide-y text-sm" data-testid="lab-sum-tests">
              {s.tests.map((t) => (
                <div key={t.name} className="flex justify-between px-3 py-1.5"><span className="font-semibold">{t.name}</span><span className="text-slate-600">{t.count} × &nbsp;{rupee(t.amount)}</span></div>
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
};
